import { defineStore } from 'pinia'
import {
  campaignKeys,
  FORCE_EMP_AIDS,
  EMP_SPLIT_AIDS,
  EMP_SPLIT_KEYS,
  DIRECT_LINK_AFFS,
  DOMAIN_AFF_MAPPING,
  getCampaignKeysForAff
} from '@/config/campaignKeys'
import { getCookie, setCookie } from '@/utils/cookies'

export const useSessionStore = defineStore('session', {
  state: () => ({
    // Session identification
    sessionId: null,

    // A/B test variant
    variant: null,
    variantId: null,

    // Form/affiliate data
    formData: {
      ip_address: '',
      aff_id: '0',
      lp_s1: '',
      lp_s2: '',
      lp_s3: '',
      lp_s4: '',
      lp_s5: '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      lp_response: 'json',
      landing_page: 'https://firstadvantage.io/app/',
      array_requested: true
    },

    // EMP (Employment) mode
    empMode: false,
    empSplitActive: false,
    empSplitKeys: null,

    // Lead tracking IDs
    LpRequestId: null,
    lpLeadId: null,
    acContactId: null,

    // Flags
    readyToRedirect: true,
    showArrayDisclaimer: false,
    isYahoo: false,

    // Location backup data
    backupCity: null,
    backupState: null,
    backupZip: null,

    // Headlines (can change based on domain)
    mainHeadline: 'Find Out If You Qualify For Economic Debt Relief in 2026',
    mainSubHeadline: 'Your search for debt relief begins here!',
    variantName: 'FFAPP-CONTROL'
  }),

  getters: {
    // Shorthand for IP address
    ipAddress: (state) => state.formData.ip_address,

    // Shorthand for affiliate ID
    affiliateId: (state) => state.formData.aff_id,

    // Get current campaign keys (respects EMP split)
    currentCampaignKeys: (state) => {
      if (state.empSplitKeys) {
        return state.empSplitKeys
      }
      return getCampaignKeysForAff(state.formData.aff_id)
    },

    // Check if current affiliate shows disclaimer
    showDisclaimer: (state) => {
      return ['kttd', 'ktt2', 'ktt3', 'ktt4', 'stt'].includes(state.formData.aff_id)
    },

    // Check if this affiliate has Everflow tracking
    hasEverflow: (state) => {
      const keys = getCampaignKeysForAff(state.formData.aff_id)
      return keys.hasOwnProperty('everflow_aff_id')
    },

    // Get Everflow config if available
    everflowConfig: (state) => {
      const keys = getCampaignKeysForAff(state.formData.aff_id)
      if (!keys.everflow_aff_id) return null
      return {
        aff_id: keys.everflow_aff_id,
        offer_id: keys.everflow_offer_id || 320,
        click_campaign_id: keys.click_campaign_id
      }
    }
  },

  actions: {
    // Initialize session from URL params and cookies
    initialize() {
      const params = new URLSearchParams(window.location.search)

      // Generate or restore session ID
      this.initSessionId()

      // Generate or restore variant
      this.initVariant()

      // Set affiliate ID from params, cookie, or domain
      this.initAffiliateId(params)

      // Parse sub parameters
      this.formData.lp_s1 = params.get('s1') || params.get('sub1') || ''
      this.formData.lp_s2 = params.get('s2') || params.get('sub2') || ''
      this.formData.lp_s3 = params.get('s3') || params.get('sub3') || ''
      this.formData.lp_s4 = params.get('s4') || params.get('sub4') || ''
      this.formData.lp_s5 = params.get('s5') || ''

      // Check if array data should be requested
      this.formData.array_requested = params.get('sa') !== '1'

      // Initialize EMP mode
      this.initEmpMode(params)

      // Set headlines based on domain
      this.initHeadlines()

      console.log('Session initialized:', this.formData)
    },

    initSessionId() {
      const key = 'session_id'
      let sessionId = localStorage.getItem(key)

      if (!sessionId) {
        if (window.crypto?.randomUUID) {
          sessionId = crypto.randomUUID()
        } else {
          // RFC4122 version 4 UUID polyfill
          sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0
            const v = c === 'x' ? r : (r & 0x3 | 0x8)
            return v.toString(16)
          })
        }
        localStorage.setItem(key, sessionId)
      }

      this.sessionId = sessionId
    },

    initVariant() {
      const key = 'landingPageVariant'
      let variant = localStorage.getItem(key)

      if (variant) {
        this.variant = JSON.parse(variant)
      } else {
        // Allowed variants
        const allowed = [
          { questionStyle: 'range', buttonStyle: 'v1', tooltip: 'off' },
          { questionStyle: 'buttons', buttonStyle: 'v1', tooltip: 'off' }
        ]

        this.variant = allowed[Math.floor(Math.random() * allowed.length)]
        localStorage.setItem(key, JSON.stringify(this.variant))
      }

      this.variantId = `${this.variant.questionStyle}-${this.variant.buttonStyle}-${this.variant.tooltip}`
      console.log('Variant:', this.variant)
    },

    initAffiliateId(params) {
      // Check domain mapping first
      const hostname = window.location.hostname
      if (DOMAIN_AFF_MAPPING[hostname]) {
        this.formData.aff_id = DOMAIN_AFF_MAPPING[hostname]
        return
      }

      // Check URL params, then cookie
      let affId = params.get('aid') || getCookie('aff_id') || '0'

      // Handle direct link affiliates (append oid)
      if (DIRECT_LINK_AFFS.includes(affId)) {
        affId = affId + (params.get('oid') || '')
      }

      // Validate affiliate exists
      if (!campaignKeys[affId]) {
        affId = '0'
      }

      this.formData.aff_id = affId

      // Save to cookie if not default
      if (affId !== '0') {
        setCookie('aff_id', affId, 7)
      }
    },

    initEmpMode(params) {
      const currentAid = this.formData.aff_id

      // Check if EMP is forced for this affiliate
      if (FORCE_EMP_AIDS.includes(currentAid)) {
        this.empMode = true
        return
      }

      // Check for 30% EMP split
      if (EMP_SPLIT_AIDS.includes(currentAid) && Math.random() < 0.30) {
        this.empMode = true
        this.empSplitActive = true
        this.empSplitKeys = EMP_SPLIT_KEYS[currentAid]
        return
      }

      // Check URL param
      if (/^(1|true|yes)$/i.test(params.get('emp') || '')) {
        this.empMode = true
      }
    },

    initHeadlines() {
      if (window.location.hostname.includes('seniors')) {
        this.mainHeadline = 'Seniors Could Qualify for Debt Relief'
        this.mainSubHeadline = "A lifetime of hard work shouldn't come with saddlebags of debt. Get back the retirement you were meant to have."
      }
    },

    // Set IP address from location service
    setIpAddress(ip) {
      this.formData.ip_address = ip || 'Unknown'
    },

    // Set location backup data
    setLocationBackup({ city, state, zip }) {
      if (city) this.backupCity = city
      if (state) this.backupState = state
      if (zip) this.backupZip = zip
    },

    // Set LP request ID (from click tracking)
    setLpRequestId(id) {
      this.LpRequestId = id
    },

    // Set LP lead ID (from lead submission)
    setLpLeadId(id) {
      this.lpLeadId = id
    },

    // Set AC contact ID
    setAcContactId(id) {
      this.acContactId = id
    },

    // Set Yahoo email flag
    setIsYahoo(isYahoo) {
      this.isYahoo = isYahoo
    },

    // Update lp_s5 (often set after Everflow tracking)
    setLpS5(value) {
      this.formData.lp_s5 = value
    },

    // Set ready to redirect flag
    setReadyToRedirect(ready) {
      this.readyToRedirect = ready
    }
  }
})
